import DatePicker from '@mui/lab/DatePicker';
import { Divider, TextField } from '@mui/material';
import {
  AdvancedSearchProps,
  SimpleCheckBox,
  SimpleSelect,
} from '@prismamedia/one-components';
import { ArticleFormat } from '__generated__/queries-web';
import clsx from 'clsx';
import { ChangeEvent, FC } from 'react';
import { NO_SOURCE_ID } from '../../utils/useGetBlockItems';
import { AuthorSelect } from './AuthorSelect';
import { CategoriesSelect } from './CategorySelect';
import { CreatorSelect } from './CreatorSelect';
import { useQualifiers } from './getQualifers.web.graphql';
import { useGetSources } from './getSource.web.graphql';
import { ImageSourceSelect } from './ImageSourceSelect';
import { useStyles } from './styles';
import { TagsInput } from './TagsInput';

export const AdvancedSearch: FC<AdvancedSearchProps> = ({
  searchParams,
  updateSearchParam,
  brandKey,
  articleFormat,
}) => {
  const classes = useStyles();
  const { data: qualifiersData } = useQualifiers(brandKey);
  const { data: sourcesData } = useGetSources(brandKey);

  return (
    <>
      <div className={classes.section}>
        <DatePicker
          className={clsx(classes.input, classes.flexInput)}
          disableFuture
          inputFormat="dd/MM/yyyy"
          label="Édité du"
          maxDate={searchParams.updatedBefore}
          onChange={updateSearchParam('updatedAfter')}
          renderInput={(props) => <TextField {...props} variant="standard" />}
          value={searchParams.updatedAfter || null}
        />
        <DatePicker
          className={clsx(classes.input, classes.flexInput)}
          disableFuture
          inputFormat="dd/MM/yyyy"
          label="Au"
          minDate={searchParams.updatedAfter}
          onChange={updateSearchParam('updatedBefore')}
          renderInput={(props) => <TextField {...props} variant="standard" />}
          value={searchParams.updatedBefore || null}
        />
        {articleFormat !== ArticleFormat.Video && (
          <CreatorSelect
            value={searchParams.creator}
            onChange={updateSearchParam('creator')}
            className={clsx(classes.input, classes.flexInput)}
          />
        )}
        {articleFormat === ArticleFormat.Rich && (
          <AuthorSelect
            value={searchParams.author}
            onChange={updateSearchParam('author')}
            className={clsx(classes.input, classes.flexInput)}
          />
        )}
      </div>
      <Divider />
      <div className={classes.section}>
        <CategoriesSelect
          brandKey={brandKey}
          articleFormat={articleFormat}
          value={searchParams.categories}
          onChange={updateSearchParam('categories')}
          className={clsx(classes.input, classes.flexInput)}
        />
        <TagsInput
          value={searchParams.tags}
          onChange={updateSearchParam('tags')}
          className={clsx(classes.input, classes.flexInput)}
        />
        <SimpleSelect
          className={clsx(classes.input, classes.flexInput)}
          label="Source de l'article"
          variant="standard"
          options={[
            { value: '', label: '-' },
            { value: NO_SOURCE_ID, label: 'Sans source' },
            ...(sourcesData?.sources.map(({ id, title }) => ({
              value: id,
              label: title,
            })) || []),
          ]}
          value={searchParams.source}
          onChange={(_, value) => updateSearchParam('source')(value)}
        />
        {articleFormat !== ArticleFormat.Slideshow && (
          <SimpleSelect
            className={clsx(classes.input, classes.flexInput)}
            label="Qualifier"
            options={[
              { value: '', label: '-' },
              { value: NO_SOURCE_ID, label: 'Sans source' },
              ...(qualifiersData?.qualifiers.map(({ id, title }) => ({
                value: id,
                label: title,
              })) || []),
            ]}
            value={searchParams.qualifier}
            onChange={(_, value) => updateSearchParam('qualifier')(value)}
          />
        )}
      </div>
      {articleFormat !== ArticleFormat.Video && (
        <>
          <Divider />
          <div className={classes.section}>
            {articleFormat === ArticleFormat.Slideshow && (
              <TextField
                label="Crédit de l'image"
                value={searchParams.imageCredit}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  updateSearchParam('imageCredit')(event.target.value);
                }}
                className={clsx(classes.input, classes.flexInput)}
                variant="standard"
              />
            )}
            <ImageSourceSelect
              value={searchParams.imageSource}
              onChange={updateSearchParam('imageSource')}
              className={clsx(classes.input, classes.flexInput)}
            />
          </div>
        </>
      )}
      {articleFormat === ArticleFormat.Rich && (
        <>
          <Divider />
          <div className={classes.section}>
            {[
              { label: 'Contient push', key: 'containPush' },
              { label: 'Contient live', key: 'containLive' },
              { label: 'Contient vidéo', key: 'containVideo' },
              { label: 'Contient diapo', key: 'containDiapo' },
            ].map(({ label, key }) => (
              <SimpleCheckBox
                key={key}
                className={classes.flexInput}
                label={label}
                checked={!!searchParams[key]}
                onChange={(value) => updateSearchParam(key)(value)}
              />
            ))}
          </div>
        </>
      )}
    </>
  );
};
